import { z } from 'zod';
import { MoneySchema } from './common.js';
// Prisma Decimal columns serialize as canonical strings; allow either string
// or a non-negative number on the wire (matches ar.ts / lva.ts / prepaid.ts).
const DecimalLike = z.union([MoneySchema, z.coerce.number().nonnegative()]);
// ============================================================
// Op Contract category catalog — spec §18 (10 known buckets)
// Free text in the DB so future additions don't require migrations; the UI
// renders by lookup and the service validates against this set.
// ============================================================
export const OP_CONTRACT_TYPES = [
    'COPIER',
    'SECURITY',
    'CLEAN',
    'WATER',
    'PEST',
    'GARDEN',
    'IT_SUPP',
    'LIFT',
    'AC',
    'INT_TEL',
];
export const OpContractTypeSchema = z.enum(OP_CONTRACT_TYPES);
// ============================================================
// Enums (mirror Prisma)
// ============================================================
export const OpContractStatusSchema = z.enum(['ACTIVE', 'EXPIRED', 'TERMINATED']);
export const OpContractAccrualStatusSchema = z.enum(['PENDING', 'POSTED', 'REVERSED']);
// M4 TFRS 16 — why a contract is NOT capitalized as a right-of-use asset.
// Mirrors the Prisma OpContractExemptionBasis enum. Nullable on the wire
// (legacy/pre-M4 rows = null = "unclassified").
export const OpContractExemptionBasisSchema = z.enum([
    'SHORT_TERM',
    'LOW_VALUE',
    'SERVICE_NON_LEASE',
]);
// ============================================================
// OpContractAccrual — child row
// ============================================================
export const OpContractAccrualSchema = z.object({
    id: z.uuid(),
    contractId: z.uuid(),
    period: z.string().regex(/^\d{6}$/, 'period must be YYYYMM'),
    amount: DecimalLike,
    status: OpContractAccrualStatusSchema,
    journalEntryId: z.uuid().nullable().optional(),
    reversalJournalEntryId: z.uuid().nullable().optional(),
    postedAt: z.coerce.date().nullable().optional(),
    postedBy: z.string().nullable().optional(),
    createdAt: z.coerce.date(),
    // M5b — voucherNo of the AP bill that trued-up this accrual (detail-view only).
    trueUpBillVoucherNo: z.string().nullable().optional(),
});
// ============================================================
// OpContractRate — M5a escalation: an effective-dated rate-schedule row.
// Each row overrides the contract's base monthlyAmount from its effectiveFrom
// (YYYYMM) period onward; the accrual reads the latest rate effective for the
// run period (else the base). A contract with no rate rows accrues the base.
// ============================================================
export const OpContractRateSchema = z.object({
    id: z.uuid(),
    contractId: z.uuid(),
    effectiveFrom: z.string().regex(/^\d{6}$/, 'effectiveFrom must be YYYYMM'),
    monthlyAmount: DecimalLike,
    note: z.string().nullable().optional(),
    createdAt: z.coerce.date(),
});
// POST /op-contracts/:id/rates — add a rate-schedule row.
export const CreateOpContractRateSchema = z.object({
    effectiveFrom: z.string().regex(/^\d{6}$/, 'effectiveFrom must be YYYYMM'),
    monthlyAmount: z.coerce.number().positive(),
    note: z.string().optional(),
});
// ============================================================
// OpContract — header
// ============================================================
export const OpContractSchema = z.object({
    id: z.uuid(),
    companyId: z.uuid(),
    code: z.string().min(1),
    contractType: z.string().min(1),
    vendorId: z.uuid().nullable().optional(),
    startDate: z.coerce.date(),
    endDate: z.coerce.date(),
    monthlyAmount: DecimalLike,
    expenseAccountCode: z.string().min(1),
    branchId: z.uuid().nullable().optional(),
    departmentId: z.uuid().nullable().optional(),
    status: OpContractStatusSchema,
    autoAccrual: z.boolean(),
    notes: z.string().nullable().optional(),
    noticePeriodDays: z.number().int().nonnegative().nullable().optional(),
    autoRenew: z.boolean(),
    renewedFromId: z.uuid().nullable().optional(),
    // M4 TFRS 16 exemption basis (null = unclassified / legacy).
    exemptionBasis: OpContractExemptionBasisSchema.nullable().optional(),
    // Engine-approval columns (M3 maker-checker). Decision-Z: separate from the
    // accounting `status`; null/0 for legacy/pre-M3 rows so they still parse.
    approvalStatus: z.string().nullable().optional(),
    currentStep: z.coerce.number().int().default(0),
    submittedBy: z.string().nullable().optional(),
    submittedAt: z.coerce.date().nullable().optional(),
    approvedBy: z.string().nullable().optional(),
    approvedAt: z.coerce.date().nullable().optional(),
    rejectedReason: z.string().nullable().optional(),
    returnedReason: z.string().nullable().optional(),
    // 'REGISTER' | 'TERMINATE' | null — discriminates which contract action is
    // awaiting approval (drives the accrual-eligibility rule + lifecycle hooks).
    pendingAction: z.string().nullable().optional(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
// List-row — adds derived display fields used by the register table.
export const OpContractListRowSchema = OpContractSchema.extend({
    vendorName: z.string().nullable().optional(),
    branchName: z.string().nullable().optional(),
    monthsTotal: z.number().int().nonnegative(),
    monthsAccrued: z.number().int().nonnegative(),
    accruedAmount: DecimalLike,
    // Set to the successor contract's code once this contract has been renewed
    // (the renewedFromId self-relation). null when not yet renewed.
    renewedToCode: z.string().nullable().optional(),
    // M4 TFRS 16 — server-derived: exemptionBasis === 'SHORT_TERM' && monthsTotal > 12.
    // A short-term lease whose per-contract term exceeds 12 months can no longer use
    // the short-term exemption and must be reassessed for capitalization.
    needsReclassification: z.boolean().optional(),
    // M5a — the rate effective for the CURRENT period from the escalation schedule
    // (falls back to the base monthlyAmount when no rate row applies yet). Display
    // only; absent on legacy rows.
    currentRate: DecimalLike.optional(),
});
// One materialized approval-chain step (M3 maker-checker, op_contract_approvals).
// Mirrors ApBillApproval — feeds the shared web ApprovalTimeline directly.
export const OpContractApprovalRowSchema = z.object({
    id: z.uuid(),
    contractId: z.uuid(),
    stepNo: z.number().int(),
    roleCode: z.string(),
    roleLabel: z.string(),
    approvedAt: z.coerce.date().nullable().optional(),
    approvedBy: z.string().nullable().optional(),
    comment: z.string().nullable().optional(),
    createdAt: z.coerce.date(),
});
// Detail — header + accruals + renewal chain + the engine approval chain.
export const OpContractWithAccrualsSchema = OpContractListRowSchema.extend({
    accruals: z.array(OpContractAccrualSchema),
    // The maker→reviewer→approver chain (stepNo asc). Empty for legacy/pre-M3 rows.
    approvals: z.array(OpContractApprovalRowSchema).optional(),
    // M5a — the contract's escalation rate schedule (effectiveFrom asc). Empty
    // when the contract has no overrides (it then accrues the base monthlyAmount).
    rates: z.array(OpContractRateSchema).optional(),
    renewedFromCode: z.string().nullable().optional(),
    renewalCodes: z.array(z.string()).optional(),
});
// ============================================================
// OpContractAccrualRun — monthly run header (M3 maker-checker, docType OSC_RUN)
// One approval per run; bucket JVs post only on final approval. Decision-Z:
// approvalStatus is the engine state machine, kept off the accrual queries.
// ============================================================
export const OpContractAccrualRunSchema = z.object({
    id: z.uuid(),
    companyId: z.uuid(),
    period: z.string().regex(/^\d{6}$/, 'period must be YYYYMM'),
    totalAmount: DecimalLike,
    contractCount: z.coerce.number().int().nonnegative().default(0),
    approvalStatus: z.string().nullable().optional(),
    currentStep: z.coerce.number().int().default(0),
    submittedBy: z.string().nullable().optional(),
    submittedAt: z.coerce.date().nullable().optional(),
    approvedBy: z.string().nullable().optional(),
    approvedAt: z.coerce.date().nullable().optional(),
    rejectedReason: z.string().nullable().optional(),
    returnedReason: z.string().nullable().optional(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
// One materialized run approval-chain step (op_contract_accrual_run_approvals).
// Mirrors OpContractApprovalRow — feeds the shared web ApprovalTimeline directly.
export const OpContractAccrualRunApprovalRowSchema = z.object({
    id: z.uuid(),
    runId: z.uuid(),
    stepNo: z.number().int(),
    roleCode: z.string(),
    roleLabel: z.string(),
    approvedAt: z.coerce.date().nullable().optional(),
    approvedBy: z.string().nullable().optional(),
    comment: z.string().nullable().optional(),
    createdAt: z.coerce.date(),
});
// Run detail — GET accrual-runs/:id returns the header + its engine approval
// chain (stepNo asc). Empty/absent for legacy rows; drives the run panel.
export const OpContractAccrualRunWithApprovalsSchema = OpContractAccrualRunSchema.extend({
    approvals: z.array(OpContractAccrualRunApprovalRowSchema).optional(),
});
// ============================================================
// POST /op-contracts — create
// ============================================================
export const CreateOpContractSchema = z
    .object({
    contractType: z.string().min(1),
    vendorId: z.uuid().nullable().optional(),
    startDate: z.iso.date(),
    endDate: z.iso.date(),
    monthlyAmount: z.coerce.number().positive(),
    // PAE 5xxx expense COA code (validated as starting with '5' in service).
    expenseAccountCode: z.string().min(1),
    branchId: z.uuid().nullable().optional(),
    departmentId: z.uuid().nullable().optional(),
    autoAccrual: z.boolean().default(true),
    noticePeriodDays: z.coerce.number().int().nonnegative().nullable().optional(),
    autoRenew: z.boolean().default(false),
    notes: z.string().optional(),
    // M4 TFRS 16 exemption basis — optional so import/legacy still work.
    exemptionBasis: OpContractExemptionBasisSchema.nullable().optional(),
})
    .refine((v) => new Date(v.startDate) < new Date(v.endDate), {
    message: 'startDate must be before endDate',
    path: ['endDate'],
});
// ============================================================
// PATCH /op-contracts/:id — partial update
// ============================================================
export const UpdateOpContractSchema = z.object({
    contractType: z.string().min(1).optional(),
    vendorId: z.uuid().nullable().optional(),
    endDate: z.iso.date().optional(),
    monthlyAmount: z.coerce.number().positive().optional(),
    expenseAccountCode: z.string().min(1).optional(),
    branchId: z.uuid().nullable().optional(),
    autoAccrual: z.boolean().optional(),
    noticePeriodDays: z.coerce.number().int().nonnegative().nullable().optional(),
    autoRenew: z.boolean().optional(),
    notes: z.string().nullable().optional(),
    // M4 TFRS 16 exemption basis — editable post-create.
    exemptionBasis: OpContractExemptionBasisSchema.nullable().optional(),
});
// ============================================================
// POST /op-contracts/:id/terminate
// ============================================================
export const TerminateOpContractSchema = z.object({
    endDate: z.iso.date(),
    notes: z.string().optional(),
});
// ============================================================
// POST /op-contracts/:id/renew — create a linked successor + terminate predecessor.
// ============================================================
export const RenewOpContractSchema = z
    .object({
    startDate: z.iso.date(),
    endDate: z.iso.date(),
    monthlyAmount: z.coerce.number().positive(),
    noticePeriodDays: z.coerce.number().int().nonnegative().nullable().optional(),
    autoRenew: z.boolean().default(false),
    notes: z.string().optional(),
})
    .refine((v) => new Date(v.startDate) < new Date(v.endDate), {
    message: 'startDate must be before endDate',
    path: ['endDate'],
});
// ============================================================
// POST /op-contracts/accrue { period }
// ============================================================
export const AccrueOpContractsSchema = z.object({
    period: z.string().regex(/^\d{6}$/, 'period must be YYYYMM'),
});
// ============================================================
// POST /op-contracts/reverse { period }
// ============================================================
export const ReverseOpContractsSchema = AccrueOpContractsSchema;
// ============================================================
// Run result — used by the run modal preview
// ============================================================
export const OpContractRunResultSchema = z.object({
    period: z.string(),
    // One JV per contractType bucket (e.g. all SECURITY contracts go into a
    // single Dr 5217 / Cr 2160 voucher).
    journalEntryIds: z.array(z.uuid()),
    journalVoucherNos: z.array(z.string()),
    contractsProcessed: z.number().int().nonnegative(),
    contractsSkipped: z.number().int().nonnegative(),
    totalAmount: DecimalLike,
    entries: z.array(OpContractAccrualSchema),
});
// ============================================================
// GET /op-contracts/disclosure?year= — TFRS/IFRS 16 para 53 disclosure
//
// Aggregates the POSTED accrual amounts (the expense actually recognized) for a
// fiscal year, grouped by exemptionBasis. Para 53(c) = short-term lease expense
// (shortTermTotal); 53(d) = low-value asset lease expense (lowValueTotal).
// SERVICE_NON_LEASE / UNCLASSIFIED are surfaced separately for completeness and
// are NOT part of the para-53 lease-exemption totals.
// ============================================================
export const OpContractDisclosureSchema = z.object({
    year: z.number().int(),
    byBasis: z.array(z.object({
        basis: z.string(), // 'SHORT_TERM'|'LOW_VALUE'|'SERVICE_NON_LEASE'|'UNCLASSIFIED'
        contractCount: z.number().int(),
        recognizedAmount: DecimalLike,
    })),
    shortTermTotal: DecimalLike,
    lowValueTotal: DecimalLike,
});
//# sourceMappingURL=op-contract.js.map